import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ShieldMoonOutlinedIcon from "@mui/icons-material/ShieldMoonOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { takedownApi } from "core/api";
import { UserRemoval } from "core/types/takedown.types";

type ChipColor = "default" | "success" | "warning" | "error";

const STATUS_KEY: Record<UserRemoval["status"], { labelKey: string; color: ChipColor }> = {
  online: { labelKey: "takedown.removals.status.online", color: "error" },
  pending: { labelKey: "takedown.removals.status.pending", color: "warning" },
  removed: { labelKey: "takedown.common.status.removed", color: "success" },
  rejected: { labelKey: "takedown.removals.status.rejected", color: "default" },
};

const CHANNEL_LABEL_KEY: Record<string, string> = {
  email_diffida: "takedown.removals.channel.email_diffida",
  pec: "takedown.removals.channel.pec",
  cloudflare: "takedown.removals.channel.cloudflare",
  host_abuse: "takedown.removals.channel.host_abuse",
  registrar_abuse: "takedown.removals.channel.registrar_abuse",
};

const fmtDate = (raw?: string | null): string => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("it-IT", { year: "numeric", month: "long", day: "numeric" });
};

/**
 * Vista end-user "Le tue rimozioni" — GET /account/removals (simple_auth, §5.3/§6.5).
 * Mostra, per ogni URL segnalato, lo stato (online/in lavorazione/rimosso) e il canale
 * che ha ottenuto la rimozione. Read-only. Da montare su una route utente (wiring: Integrator).
 */
export const UserRemovals: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<UserRemoval[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await takedownApi.getRemovals();
        if (active) setRows(Array.isArray(data) ? data : []);
      } catch (e: any) {
        if (active) setError(t("takedown.removals.error_load", { error: e?.message || e }));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const stats = useMemo(() => {
    const removed = rows.filter((r) => r.status === "removed").length;
    const pending = rows.filter((r) => r.status === "pending").length;
    return { removed, pending, total: rows.length };
  }, [rows]);

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <ShieldMoonOutlinedIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          {t("takedown.removals.title")}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("takedown.removals.description")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && rows.length > 0 && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip color="success" label={t("takedown.removals.stat_removed", { count: stats.removed })} />
          <Chip color="warning" label={t("takedown.removals.stat_pending", { count: stats.pending })} />
          <Chip variant="outlined" label={t("takedown.removals.stat_total", { count: stats.total })} />
        </Stack>
      )}

      <Paper sx={{ borderRadius: "10px", p: "12px" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={56} color="secondary" />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t("takedown.removals.table.content")}</TableCell>
                  <TableCell>{t("takedown.removals.table.status")}</TableCell>
                  <TableCell>{t("takedown.removals.table.channel")}</TableCell>
                  <TableCell>{t("takedown.removals.table.removed_at")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ "& .MuiTableCell-root": { padding: "10px", fontSize: "14px" } }}>
                {rows.map((r, i) => {
                  const smMeta = STATUS_KEY[r.status];
                  const sm = smMeta
                    ? { label: t(smMeta.labelKey), color: smMeta.color }
                    : { label: r.status, color: "default" as ChipColor };
                  return (
                    <TableRow key={`${r.url}-${i}`} hover>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Link
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, wordBreak: "break-all" }}
                        >
                          {r.domain || r.url}
                          <OpenInNewRoundedIcon fontSize="inherit" />
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={sm.color} label={sm.label} />
                      </TableCell>
                      <TableCell>
                        {r.channel ? (CHANNEL_LABEL_KEY[r.channel] ? t(CHANNEL_LABEL_KEY[r.channel]) : r.channel) : "—"}
                      </TableCell>
                      <TableCell>{fmtDate(r.removed_at)}</TableCell>
                    </TableRow>
                  );
                })}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                        {t("takedown.removals.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default UserRemovals;
